/**
 * ConnectAreaPicker — composer pill to scope the chat to an Ando Connect area.
 *
 * June 14, 2026. Ando Connect is the CONFIG surface (create areas, grant
 * sources, invite, publish); the actual CONSUMPTION happens HERE, in the normal
 * chat. This pill lets the user pick one shared area they can read (owned OR
 * shared-with-me) as a DELIBERATE, EXCLUSIVE query scope ("solo area"): once
 * selected, every turn answers ONLY from that area's granted, read-only slice
 * (the BE re-validates membership + elevates to the owner tenant). Deselecting
 * ("Esci dall'area") restores the user's own knowledge.
 *
 * Why its OWN pill (not a section inside ``SourceScopePicker``): folders/files/
 * connectors there are ADDITIVE narrowings of the user's own corpus; a Connect
 * area is an EXCLUSIVE replacement. Mixing the two semantics in one control
 * would mislead. The chrome mirrors ``DeepModePill`` (another answer-contract
 * flag) so the composer's visual language stays unified.
 *
 * Controlled: the caller owns ``selectedId`` + ``onSelect`` so the choice
 * persists on the per-conversation config (``config.connectSpace``); the
 * read-only "Area · …" chip in the ChipBar renders off the same state.
 *
 * A11y — the trigger is a labelled button; the active area name is reflected in
 * ``aria-label`` so screen readers announce the current scope.
 */

"use client";

import { AnimatePresence, motion, useReducedMotion } from "framer-motion";
import { Blend, Check, Plus, X } from "lucide-react";
import * as React from "react";

import { cn } from "../../lib/cn";
import { Popover, PopoverContent, PopoverTrigger } from "../../primitives/popover";

/** One selectable Connect area (owned or shared-with-me). */
export interface ConnectAreaOption {
  /** ``connect_spaces.id``. */
  readonly id: string;
  /** Area display name. */
  readonly name: string;
  /** Owning org name for a shared area; ``null`` for the user's own areas. */
  readonly ownerOrgName?: string | null;
}

export interface ConnectAreaPickerProps {
  /** Areas the user may query (owned + shared-with-me), already merged. */
  readonly areas: readonly ConnectAreaOption[];
  /** Currently scoped area id, or ``null`` for normal chat. */
  readonly selectedId: string | null;
  /** Fired with the chosen area (``null`` = exit the area → own knowledge). */
  readonly onSelect: (area: ConnectAreaOption | null) => void;
  /** True while the areas list is loading. */
  readonly loading?: boolean;
  /** Imperative lock — e.g. while a run is streaming. */
  readonly disabled?: boolean;
  /** "Vai a Connect" escape hatch (empty state / manage). */
  readonly onManage?: () => void;
  readonly className?: string;
}

const MAX_LABEL = 18;

function truncate(s: string): string {
  return s.length > MAX_LABEL ? `${s.slice(0, MAX_LABEL - 1)}…` : s;
}

export const ConnectAreaPicker: React.FC<ConnectAreaPickerProps> = ({
  areas,
  selectedId,
  onSelect,
  loading = false,
  disabled = false,
  onManage,
  className,
}) => {
  const reduceMotion = useReducedMotion();
  const [open, setOpen] = React.useState(false);
  const [query, setQuery] = React.useState("");

  const selected = React.useMemo(
    () => areas.find((a) => a.id === selectedId) ?? null,
    [areas, selectedId],
  );
  const active = selectedId !== null;

  const filtered = React.useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return areas;
    return areas.filter(
      (a) => a.name.toLowerCase().includes(q) || (a.ownerOrgName ?? "").toLowerCase().includes(q),
    );
  }, [areas, query]);

  const choose = (area: ConnectAreaOption | null) => {
    onSelect(area);
    setOpen(false);
    setQuery("");
  };

  // The pill shows the active area NAME when scoped (so the current scope is
  // legible at a glance), else the neutral "Area" affordance.
  const label = active && selected ? truncate(selected.name) : "Area";

  return (
    <Popover open={open} onOpenChange={disabled ? undefined : setOpen}>
      <PopoverTrigger asChild>
        <motion.button
          type="button"
          aria-haspopup="listbox"
          aria-label={
            active && selected
              ? `Interroga l'area condivisa: ${selected.name}`
              : "Interroga un'area condivisa"
          }
          // Compact toolbar: icon-only when no area is scoped → a native
          // tooltip names the control (the trigger is a popover, so we use
          // ``title`` rather than nesting a hover-Tooltip in the click-trigger).
          title={active ? undefined : "Interroga un'area"}
          disabled={disabled}
          initial={false}
          animate={reduceMotion ? undefined : active ? { scale: [1, 1.04, 1] } : { scale: 1 }}
          transition={{ duration: 0.36, ease: [0.16, 1, 0.3, 1] }}
          className={cn(
            // Icon-only minimal button aligned with the other composer icon
            // controls: h-8, rounded-lg, borderless. Expands to a soft-orange
            // pill with the area label once an area is scoped.
            "inline-flex h-8 shrink-0 items-center gap-1.5 rounded-lg",
            "max-w-[180px] font-medium text-[11.5px] transition-colors",
            "focus-visible:outline-none focus-visible:ring-2",
            "focus-visible:ring-ando-orange/40 focus-visible:ring-offset-1",
            active
              ? "bg-ando-orange-soft px-2.5 text-ando-orange"
              : "w-8 justify-center bg-transparent text-ink-3 hover:bg-black/[0.045] hover:text-ink-1",
            disabled && "cursor-not-allowed opacity-50 hover:bg-transparent",
            className,
          )}
        >
          <Blend size={14} strokeWidth={1.85} aria-hidden />
          {active ? <span className="truncate">{label}</span> : null}
        </motion.button>
      </PopoverTrigger>

      <PopoverContent
        side="top"
        align="start"
        sideOffset={8}
        className="w-[280px] overflow-hidden p-0"
      >
        <div className="border-hairline/70 border-b px-3 py-2.5">
          <p className="font-semibold text-[12px] text-ink-1">Interroga un'area</p>
          <p className="mt-0.5 text-[10.5px] text-ink-2 leading-snug">
            Rispondo solo dalle fonti condivise nell'area scelta, in sola lettura.
          </p>
        </div>

        {/* Exit-the-area row — only when an area is currently scoped. */}
        {active ? (
          <button
            type="button"
            onClick={() => choose(null)}
            className={cn(
              "flex w-full items-center gap-2 border-hairline/60 border-b px-3 py-2 text-left text-[12px] text-ink-2 transition-colors",
              "hover:bg-cream-deep hover:text-ink-1 focus-visible:outline-none focus-visible:bg-cream-deep",
            )}
          >
            <X size={13} strokeWidth={2} aria-hidden />
            Esci dall'area · torna alla tua conoscenza
          </button>
        ) : null}

        {areas.length > 6 ? (
          <div className="border-hairline/60 border-b px-2.5 py-2">
            <input
              type="text"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Cerca un'area…"
              aria-label="Cerca un'area"
              className={cn(
                "w-full rounded-md border border-black/10 bg-white px-2.5 py-1.5 text-[12px] text-ink-1",
                "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ando-orange/40",
              )}
            />
          </div>
        ) : null}

        <div className="max-h-60 overflow-y-auto py-1">
          {loading ? (
            <p className="px-3 py-3 text-[12px] text-ink-2">Caricamento aree…</p>
          ) : areas.length === 0 ? (
            <div className="px-3 py-4 text-center">
              <p className="text-[12px] text-ink-2">Nessuna area condivisa disponibile.</p>
              {onManage ? (
                <button
                  type="button"
                  onClick={() => {
                    onManage();
                    setOpen(false);
                  }}
                  className="mt-2 inline-flex items-center gap-1 font-medium text-[11.5px] text-ando-orange hover:underline"
                >
                  <Plus size={12} strokeWidth={2.2} aria-hidden />
                  Vai a Connect
                </button>
              ) : null}
            </div>
          ) : filtered.length === 0 ? (
            <p className="px-3 py-3 text-[12px] text-ink-2">Nessun risultato.</p>
          ) : (
            <AnimatePresence initial={false}>
              {filtered.map((a) => {
                const isActive = a.id === selectedId;
                return (
                  <button
                    key={a.id}
                    type="button"
                    aria-pressed={isActive}
                    onClick={() => choose(a)}
                    className={cn(
                      "flex w-full items-center gap-2.5 px-3 py-2 text-left transition-colors",
                      "focus-visible:outline-none focus-visible:bg-cream-deep",
                      isActive ? "bg-ando-orange-soft/45" : "hover:bg-cream-deep",
                    )}
                  >
                    <span
                      aria-hidden
                      className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-cream-deep text-ink-2"
                    >
                      <Blend size={12} strokeWidth={1.75} />
                    </span>
                    <span className="min-w-0 flex-1">
                      <span className="block truncate font-medium text-[12px] text-ink-1">
                        {a.name}
                      </span>
                      {a.ownerOrgName ? (
                        <span className="block truncate text-[10.5px] text-ink-2">
                          {a.ownerOrgName}
                        </span>
                      ) : null}
                    </span>
                    {isActive ? (
                      <Check
                        size={14}
                        strokeWidth={2.4}
                        aria-hidden
                        className="shrink-0 text-ando-orange"
                      />
                    ) : null}
                  </button>
                );
              })}
            </AnimatePresence>
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
};
ConnectAreaPicker.displayName = "ConnectAreaPicker";
